import { md5Base62 } from './md5Base62'
import { i18n, TFunction } from 'i18next'
import { fallbackToEnglishExceptForFrench } from './fallbackToEnglishExceptForFrench'
import Vue, { computed, ComputedRef, Ref, ref } from 'vue'

const hashKeyByText: Map<string, string> = new Map()

/**
 * Converts some text to a hash that can be used as a translation key.
 * @param text The text to convert to a hash key
 * @returns A MD5 hash encoded in base62
 */
const textToHashKey = (text: string): string => {
    let hashKey = hashKeyByText.get(text)

    if (hashKey === undefined) {
        hashKey = md5Base62(text)
        hashKeyByText.set(text, hashKey)
    }

    return hashKey
}

/**
 * Tries to translate some text by using its md5 base62 hash as the translation key.
 * @param $t Translation function from i18next
 * @param namespace The translation namespace
 * @param text The text to translate
 * @returns The translated text or the original text if no translation is found.
 */
export const hashTranslate = ($t: TFunction, namespace: string, text: string | undefined): string =>
    text
        ? $t(textToHashKey(text), {
              ns: namespace,
              defaultValue: text,
              fallbackLng: fallbackToEnglishExceptForFrench,
          })
        : ''

let translation: Translation | undefined

export function useTranslation(): Translation {
    if (!translation) {
        const vue = new Vue()
        const i18next = vue.$i18next
        const t = ref<TFunction>(i18next.t)

        i18next.on('loaded', () => {
            t.value = ((...args: Parameters<TFunction>) => vue.$i18next.t(...args)) as TFunction
        })

        const tHash = computed<THashFunction>(() => {
            const $t = t.value
            return (namespace, text) => hashTranslate($t, namespace, text)
        })

        const tDefault = computed<TDefaultFunction>(() => {
            const $t = t.value
            return (namespace, key, defaultValue) =>
                $t(key, {
                    ns: namespace,
                    defaultValue,
                    fallbackLng: fallbackToEnglishExceptForFrench,
                })
        })

        translation = {
            t: t,
            tHash,
            tDefault,
            i18next,
        }
    }

    return translation
}

export function useTranslate() {
    const { t } = useTranslation()
    return t
}

export interface Translation {
    /**
     * Reference to the i18next translation function.
     * Updated when the available translations change.
     */
    t: Ref<TFunction>
    /**
     * Reference to a translation function
     */
    tHash: ComputedRef<THashFunction>
    tDefault: ComputedRef<TDefaultFunction>
    i18next: i18n
}

export interface THashFunction {
    /**
     * Tries to find a translation using the Base62 MD5 hash of the given text as the key
     * @param namespace Namespace where the translation is located
     * @param text Text to translate
     * @returns The translated text if found or the original text if not found
     */
    (namespace: string, text: string | undefined): string
}

export interface TDefaultFunction {
    /**
     * Tries to find a translation or returns a default if not found
     * @param namespace Namespace where the translation is located
     * @param key Text to translate
     * @param defaultValue The text to show if no translation is found.
     */
    (namespace: string, key: string, defaultValue: string): string
}
